import classNames from 'classnames';
import Typography from 'components/generics/texts/Typography';
import { useLandaRouter } from 'hooks/logics/useLandaRouter';
import usePortfolio from 'hooks/requests/portfolio/usePortfolio';
import { NOTIFICATION_ITEMS } from 'pages/profile/components/ProfileNotifications/utils';
import { FC, HTMLAttributes, useMemo } from 'react';
import classes from './index.module.scss';
import useResidentDetails from '../../../../hooks/requests/resident/useResidentDetails';
import {
  EVICTION,
  IDENTITY_MISMATCH,
  INCOMPLETE_ONBOARDING,
  LEASE_END,
  PAYMENT_FAILED,
} from '../../../../consts/idsContent';
import useFetchContentByIds from '../../../../hooks/requests/content/useFetchContentByIds';
import CircledBanIcon from '../../../../public/assets/icons/circled_ban.svg';
import ArrowRight from '../../../../public/assets/icons/arrow_right.svg';
import WarningIcon from 'public/assets/icons/warning_icon.svg';
import { replaceDatePlaceholder } from '../../../../utils/formatters';
import { LANDA_APP } from '../../../../consts/routes/urls';
import useVerificationStatus from 'hooks/requests/kyc/useVerificationStatus';
import useUserDetails from 'hooks/requests/user/hooks/useUserDetails';
import { USER_STATUS } from 'consts/User/status';

const isParseble = (json: string) => {
  try {
    JSON.parse(json);
    return true;
  } catch (error) {
    return false;
  }
};
const ProfileNotifications = ({
  className,
}: HTMLAttributes<HTMLDivElement>) => {
  const { navigate } = useLandaRouter();
  const { portfolio } = usePortfolio();
  const { residentDetails, failedPayments, isActiveLeaseEviction } =
    useResidentDetails();
  const { router } = useLandaRouter();

  const contentIds = [];
  const hasUnitTicker = residentDetails?.renewalUnitsTicker?.length > 0;
  if (hasUnitTicker) contentIds.push(LEASE_END);
  if (failedPayments.length > 0) contentIds.push(PAYMENT_FAILED);
  if (isActiveLeaseEviction) contentIds.push(EVICTION);

  const { verificationStatus } = useVerificationStatus();
  const { userDetails } = useUserDetails();

  const comment = useMemo(() => {
    if (
      verificationStatus &&
      verificationStatus?.idology &&
      isParseble(verificationStatus?.idology)
    ) {
      return JSON.parse(verificationStatus?.idology ?? '');
    }
    return {};
  }, [verificationStatus]);

  const identityMismatch = useMemo(() => {
    return comment?.qualifiers?.length > 1;
  }, [comment]);

  const incompleteOnboarding = useMemo(() => {
    return userDetails?.userStage === USER_STATUS.ACCOUNT_BEFORE_KYC;
  }, [userDetails]);

  const { content } = useFetchContentByIds(contentIds);

  const getContentDataById = (contentId: string) =>
    content?.contents?.find((item: any) => item.contentId === contentId) || {};

  const renderNotifications = useMemo(() => {
    const notifications = [];

    if (hasUnitTicker) {
      const contentData = getContentDataById(LEASE_END);
      notifications.push({
        className: classNames(classes.notification, classes.pressable),
        icon: CircledBanIcon,
        iconClassName: classes.icon,
        title: contentData.title || 'Your lease will end soon',
        titleClassName: classes.title,
        subtitle: contentData?.subtitle
          ? replaceDatePlaceholder(contentData.subtitle, '')
          : 'Your lease ends soon. Please let us know if you intend to extend or move out.',
        subtitleClassName: classes.subtitle,
        action: contentData.body || 'Renew lease',
        actionClassName: classes.action,
        actionIcon: ArrowRight,
        actionIconClassName: classes.actionIcon,
        onClick: () => {
          router.replace(
            `/residents/sign-lease?unitTicker=${residentDetails?.renewalUnitsTicker[0]}`
          );
        },
      });
    }

    if (failedPayments.length > 0) {
      const contentData = getContentDataById(PAYMENT_FAILED);
      notifications.push({
        className: classNames(classes.notificationRed, classes.pressable),
        icon: WarningIcon,
        iconClassName: classes.icon,
        title: contentData.title || 'Your payment failed',
        titleClassName: classes.title,
        subtitle:
          contentData?.subtitle ||
          'Your ACH Payment failed. In order to make your next payment and avoid stop eviction process, you will need to wire the  funds and send us the wire confirmation.',
        subtitleClassName: classes.subtitle,
        action: contentData.body || 'Click here for wire information',
        actionClassName: classes.action,
        actionIcon: ArrowRight,
        actionIconClassName: classes.actionIcon,
        onClick: () => {
          navigate(`${LANDA_APP}/rent-wire-information`);
        },
      });
    }
    if (isActiveLeaseEviction) {
      const contentData = getContentDataById(EVICTION);
      const email = contentData?.info || 'verification@landa.app';

      notifications.push({
        className: classNames(classes.notificationRed, classes.pressable),
        icon: WarningIcon,
        iconClassName: classes.icon,
        title: contentData.title || 'You are in eviction process',
        titleClassName: classes.title,
        subtitle:
          contentData?.subtitle ||
          'Your account has been suspended due to an issue with your account please contact us for support in unlocking your account.',
        subtitleClassName: classes.subtitle,
        action: contentData.body || 'Verification@landa.app',
        actionClassName: classes.action,
        actionIcon: ArrowRight,
        actionIconClassName: classes.actionIcon,
        onClick: () => {
          window.location.href = `mailto:${email}`;
        },
      });
    }

    if (incompleteOnboarding) {
      const contentData = getContentDataById(INCOMPLETE_ONBOARDING);
      notifications.push({
        className: classNames(classes.notification, classes.pressable),
        icon: CircledBanIcon,
        iconClassName: classes.icon,
        title: contentData?.title || 'Incomplete Onboarding',
        titleClassName: classes.title,
        subtitle:
          contentData?.subtitle ||
          'You didnt complete the onboarding process. Please finish all required steps to continue.',
        subtitleClassName: classes.subtitle,
        action: contentData?.body || 'Complete Onboarding',
        actionClassName: classes.action,
        actionIcon: ArrowRight,
        actionIconClassName: classes.actionIcon,
        onClick: () => {
          router.replace(`/kyc`);
        },
      });
    }

    if (identityMismatch) {
      const contentData = getContentDataById(IDENTITY_MISMATCH);
      notifications.push({
        className: classNames(classes.notification, classes.pressable),
        icon: CircledBanIcon,
        iconClassName: classes.icon,
        title: contentData?.title || 'ID Mismatch',
        titleClassName: classes.title,
        subtitle:
          contentData?.subtitle ||
          'Your ID information doesnt match. Please review the information you entered and update accordingly.',
        subtitleClassName: classes.subtitle,
        action: contentData?.body || 'Complete verification',
        actionClassName: classes.action,
        actionIcon: ArrowRight,
        actionIconClassName: classes.actionIcon,
        onClick: () => {
          router.replace(`/kyc`);
        },
      });
    }

    const otherNotifications = NOTIFICATION_ITEMS.filter((notification) =>
      notification.condition({ portfolio })
    );

    notifications.push(...otherNotifications);

    return notifications;
  }, [
    hasUnitTicker,
    content,
    portfolio,
    residentDetails?.renewalUnitsTicker,
    router,
  ]);
  if (!renderNotifications.length) return null;
  return (
    <div>
      {renderNotifications.map((notification, index) => (
        <div
          key={notification.title + index}
          className={classNames(notification.className, className)}
          onClick={() => notification.onClick(navigate)}
        >
          {notification.title && (
            <div className={classes.titleContainer}>
              <notification.icon className={notification.iconClassName} />
              <Typography className={notification.titleClassName}>
                {notification.title}
              </Typography>
            </div>
          )}
          {notification.subtitle && (
            <Typography className={notification.subtitleClassName}>
              {notification.subtitle}
            </Typography>
          )}
          {notification.action && (
            <div className={classes.actionContainer}>
              <Typography className={notification.actionClassName}>
                {notification.action}
              </Typography>
              <notification.actionIcon
                className={notification.actionIconClassName}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProfileNotifications;
